import MY_CONFIG from "@/constants";
import {
  Search,
  Bookmark,
  Bell,
  Building2,
  ArrowRight,
  Smartphone,
  Download,
  Star,
} from "lucide-react";

export default function MobileAppLandingPage() {

  const previews = [
    { type: "image", src: "https://storage.vehidhi.com/mobile/screenshots/IMG_6806.PNG" },
    { type: "image", src: "https://storage.vehidhi.com/mobile/screenshots/IMG_6807.PNG" },
    { type: "video", src: "https://storage.vehidhi.com/mobile/screenshots/appstore_final1.mp4" },
    { type: "image", src: "https://storage.vehidhi.com/mobile/screenshots/IMG_6815.PNG" },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16">
        <div className="text-center">

          <img
            src={MY_CONFIG.LOGO_URL}
            alt={MY_CONFIG.APP_NAME}
            className="w-24 h-24 mx-auto mb-6 rounded-2xl shadow-sm"
          />

          <h1 className="text-4xl md:text-6xl font-black tracking-tight">
            {MY_CONFIG.APP_NAME}
          </h1>

          <p className="max-w-2xl mx-auto mt-6 text-lg md:text-xl text-gray-600 leading-relaxed">
            Discover government announcements, tenders, job opportunities,
            public notices, and official publications — all in one powerful mobile app.
          </p>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href="#screens"
              className="bg-black text-white px-6 py-3 rounded-2xl flex items-center gap-2 font-semibold hover:opacity-90 transition"
            >
              View App Screenshots <ArrowRight size={18} />
            </a>

            <a
              href="/contact"
              className="border px-6 py-3 rounded-2xl font-semibold hover:bg-gray-50"
            >
              Contact Us
            </a>
            <a
              href="/privacypolicy"
              className="border px-6 py-3 rounded-2xl font-semibold hover:bg-gray-50"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </section>


      {/* APP SCREENSHOTS */}
      <section id="screens" className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">

          <h2 className="text-3xl font-bold mb-3">
            App Preview
          </h2>

          <p className="text-gray-600 mb-10">
            A glimpse of how the app looks on mobile
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {previews.map((item, index) => (
              <AppPreview key={index} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">

          <h2 className="text-center text-3xl font-bold mb-12">
            Powerful Features
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">

            <FeatureCard
              icon={<Search size={26} />}
              title="Smart Search"
              description="Find announcements instantly with advanced filtering."
            />

            <FeatureCard
              icon={<Bookmark size={26} />}
              title="Save & Bookmark"
              description="Save important notices for quick access later."
            />

            <FeatureCard
              icon={<Building2 size={26} />}
              title="Official Sources"
              description="Verified government publications and notices."
            />

            {/* <FeatureCard
              icon={<Bell size={26} />}
              title="Instant Alerts"
              description="Get notified when new updates are published."
            /> */}

          </div>
        </div>
      </section>

      {/* APP STATS */}
      <section className="bg-black text-white py-20">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-10 text-center">

          <Stat number="10K+" label="Active Users" />
          <Stat number="500+" label="Daily Updates" />
          <Stat number="99%" label="User Satisfaction" />

        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section className="py-20 text-center">
        <div className="max-w-3xl mx-auto px-6">

          <Smartphone className="mx-auto mb-4 text-blue-600" size={40} />

          <h2 className="text-3xl font-bold mb-4">
            Get the App Experience
          </h2>

          <p className="text-gray-600 mb-8">
            Access all announcements anytime, anywhere from your mobile device.
          </p>

          <button className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-semibold flex items-center gap-2 mx-auto hover:bg-blue-700 transition">
            <Download size={18} />
            Download Now
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 text-center text-gray-500 border-t">
        © 2026 {MY_CONFIG.APP_NAME}. All Rights Reserved.
      </footer>

    </div>
  );
}

/* SCREENSHOT CARD */
function AppScreen({ src }: { src: string }) {
  return (
    <div className="rounded-3xl overflow-hidden shadow-lg border bg-white hover:-translate-y-1 transition">
      <img src={src} className="w-full h-auto" />
    </div>
  );
}

/* FEATURE CARD */
function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border hover:shadow-md transition">
      <div className="text-blue-600 mb-3">{icon}</div>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

/* STATS */
function Stat({ number, label }: { number: string; label: string }) {
  return (
    <div>
      <h3 className="text-4xl font-bold">{number}</h3>
      <p className="text-white/70 mt-2">{label}</p>
    </div>
  );
}

function AppPreview({
  item,
}: {
  item: { type: "image" | "video"; src: string };
}) {
  return (
    <div className="rounded-3xl overflow-hidden shadow-lg border bg-white hover:-translate-y-1 transition relative group">

      {item.type === "image" ? (
        <img src={item.src} className="w-full h-auto" />
      ) : (
        <video
          src={item.src}
          className="w-full h-auto"
          autoPlay
          muted
          loop
          playsInline
        />
      )}

      {/* Play badge for video */}
      {item.type === "video" && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black/50 text-white px-3 py-2 rounded-full text-sm">
            ▶ Preview
          </div>
        </div>
      )}
    </div>
  );
}